import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCart, updateCartItem, clearCart, getProduct } from '../api/api';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft } from 'lucide-react';

const Cart = ({ sessionId }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      loadCart();
    }
  }, [sessionId]);

  const loadCart = async () => {
    try {
      const cartData = await getCart(sessionId);
      setCart(cartData);

      // Load product details for each cart item
      const productDetails = {};
      for (const item of cartData.items || []) {
        const product = await getProduct(item.product_id);
        productDetails[item.product_id] = product;
      }
      setProducts(productDetails);
      setLoading(false);
    } catch (error) {
      console.error('Error loading cart:', error);
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (productId, weightOrColor, newQuantity, isColor = false) => {
    try {
      if (isColor) {
        await updateCartItem(sessionId, productId, newQuantity, null, weightOrColor);
      } else {
        await updateCartItem(sessionId, productId, newQuantity, weightOrColor);
      }
      await loadCart();
    } catch (error) {
      console.error('Error updating cart:', error);
      alert('Sepet güncellenirken bir hata oluştu.');
    }
  };

  const handleRemoveItem = async (productId, weightOrColor, isColor = false) => {
    await handleUpdateQuantity(productId, weightOrColor, 0, isColor);
  };

  const handleClearCart = async () => {
    if (window.confirm('Sepeti temizlemek istediğinize emin misiniz?')) {
      try {
        await clearCart(sessionId);
        await loadCart();
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    }
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-44 flex justify-center items-center">
        <div className="loading-coffee">
          <div></div>
          <div></div>
        </div>
      </div>
    );
  }

  const isEmpty = !cart || !cart.items || cart.items.length === 0;

  return (
    <div className="min-h-screen pt-44 pb-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/products"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-amber-600 mb-4 transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Alışverişe Devam Et</span>
          </Link>
          <h1 className="text-4xl font-bold text-gray-900" data-testid="cart-title">Sepetim</h1>
        </div>

        {isEmpty ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center" data-testid="empty-cart">
            <ShoppingBag className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sepetiniz Boş</h2>
            <p className="text-gray-600 mb-8">Henüz sepetinize ürün eklemediniz.</p>
            <Link
              to="/products"
              className="btn-primary inline-block bg-gradient-to-r from-junto-red to-junto-red-dark hover:from-junto-red-dark hover:to-junto-red-light text-white font-bold py-3 px-8 rounded-xl transition-all"
              data-testid="shop-now-btn"
            >
              Alışverişe Başla
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {cart.items.map((item, index) => {
                const product = products[item.product_id];
                if (!product) return null;

                // Espresso makinesi için renk bazlı resim seçimi
                const getProductImage = () => {
                  if (product.category === 'Espresso Makineleri' && product.name.includes('Fiamma Compass')) {
                    if (item.color === 'Beyaz') {
                      return '/images/fiamma-compass-white.jpg';
                    } else {
                      return '/images/fiamma-compass-black.jpg';
                    }
                  }
                  // Diğer ürünler için mevcut image kullan
                  return product.image;
                };

                return (
                  <div
                    key={`${item.product_id}-${item.weight || item.color}`}
                    className="bg-white rounded-xl shadow-lg p-6 fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <div className="flex items-center space-x-6">
                      <img
                        src={getProductImage()}
                        alt={product.name}
                        className="w-24 h-24 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/junto-logo.png';
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {product.name}
                        </h3>
                        <div className="space-y-1 mb-2">
                          {item.weight && (
                            <>
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">Gramaj:</span> {item.weight}
                              </p>
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">Öğütme:</span> {item.grind || 'Çekirdek'}
                              </p>
                            </>
                          )}
                          {item.color && (
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Renk:</span> {item.color}
                            </p>
                          )}
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">Adet:</span> {item.quantity}
                          </p>
                        </div>
                        <p className="text-xl font-bold text-junto-red">
                          ₺{item.price.toFixed(2)} × {item.quantity} = ₺{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex flex-col items-end space-y-4">
                        <button
                          onClick={() => handleRemoveItem(item.product_id, item.weight || item.color, !!item.color)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.weight || item.color, item.quantity - 1, !!item.color)}
                            className="w-8 h-8 bg-white hover:bg-gray-200 rounded-lg font-bold transition-colors"
                            disabled={item.quantity <= 1}
                            data-testid={`decrease-quantity-${item.product_id}`}
                          >
                            <Minus className="w-4 h-4 mx-auto" />
                          </button>
                          <span className="text-lg font-bold w-8 text-center" data-testid={`quantity-${item.product_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleUpdateQuantity(item.product_id, item.weight || item.color, item.quantity + 1, !!item.color)}
                            className="w-8 h-8 bg-white hover:bg-gray-200 rounded-lg font-bold transition-colors"
                            data-testid={`increase-quantity-${item.product_id}`}
                          >
                            <Plus className="w-4 h-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                onClick={handleClearCart}
                className="w-full text-center text-red-600 hover:text-red-700 font-medium py-2"
                data-testid="clear-cart-btn"
              >
                Sepeti Temizle
              </button>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-xl p-6 sticky top-44" data-testid="order-summary">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Sipariş Özeti</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Ara Toplam</span>
                    <span data-testid="subtotal">₺{cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Kargo</span>
                    <span className="text-green-600 font-medium">Ücretsiz</span>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-xl font-bold text-gray-900">
                      <span>Toplam</span>
                      <span className="text-amber-600" data-testid="total">₺{cart.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full btn-primary bg-gradient-to-r from-junto-red to-junto-red-dark hover:from-junto-red-dark hover:to-junto-red-light text-white font-bold py-4 px-8 rounded-xl transition-all mb-4"
                  data-testid="checkout-btn"
                >
                  Siparişi Tamamla
                </button>

                <Link
                  to="/products"
                  className="block w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-8 rounded-xl transition-all"
                >
                  Alışverişe Devam Et
                </Link>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 text-center">
                    🚚 <strong>Ücretsiz kargo</strong> tüm siparişlerde geçerlidir
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;